import {Button, FormControl, FormControlLabel, InputLabel, MenuItem, Select, Switch} from '@material-ui/core';
import {hideModal} from 'Core/ducks/modal';
import {Loaded, toEither} from 'Core/models/Async';
import {identity} from 'fp-ts/lib/function';
import {fromArray} from 'fp-ts/lib/NonEmptyArray';
import * as O from 'fp-ts/lib/Option';
import {pipe} from 'fp-ts/lib/pipeable';
import React, {useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {ReducerState} from 'rootReducer';
import Modal from 'Shared/components/Modal';
import withUserClaims, {WithClaimsProps} from 'Shared/components/withUserClaims';
import {updateDefaultAgencyAssociation} from 'Users/ducks/userAccount';
import {setSelectedAgency} from 'Users/ducks/userProfile';
import {AgentClaims} from 'Users/models/AgentClaims';
import {EnrollTrackClaims} from 'Users/models/Claims';
import {UserClaims, UserTypeClaimKey, UserTypeClaims} from 'Users/models/Claims';
import * as E from 'fp-ts/lib/Either';
import './SwitchTaxIdModal.sass';
import {AgentAgencyClaim} from 'Users/models/AgentAgencyClaim';

type SwitchTaxIdModalProps = {
  agencyAssociations: Array<AgentAgencyClaim>;
  onClose: typeof hideModal;
} & WithClaimsProps;

const SwitchTaxIdModal = (props: SwitchTaxIdModalProps) => {
  const dispatch = useDispatch();
  const {selectedAgency, userClaims} = useSelector((state: ReducerState) => state.user.profile);
  const data = toEither(userClaims);
  const claims: UserClaims | undefined = pipe(
    data,
    E.map(value => new Loaded(value)),
    E.fold(
      () => undefined,
      value => value.Data
    )
  )!;
  let [isImpersonatingEnrollTrackUser, isImpersonatingAgentUser] = [false, false];

  claims.userTypes.map(
    UserTypeClaims.match({
      [UserTypeClaimKey.AgentSite]: (agentClaims: AgentClaims) => {
        if (agentClaims.impersonatedAgentClaim) {
          isImpersonatingAgentUser = true;
        }
      },

      [UserTypeClaimKey.EnrollTrack]: (enrollTrackClaims: EnrollTrackClaims) => {
        if (enrollTrackClaims.impersonatedEnrollTrackClaim) {
          isImpersonatingEnrollTrackUser = true;
        }
      },
      default: () => false,
    })
  );
  const isImpersonating = isImpersonatingEnrollTrackUser || isImpersonatingAgentUser;

  const currentTaxIdNumber = pipe(
    selectedAgency,
    O.fold(
      () => '',
      agency => agency.encryptedIdentifier
    )
  );
  const currentNyFilteringStatus = pipe(
    selectedAgency,
    O.fold(
      () => false,
      agency => agency.isNewYork || false
    )
  );

  const [showNy, setShowNy] = useState<boolean>(currentNyFilteringStatus || false);
  const [selectedAccount, setAccount] = useState(currentTaxIdNumber);

  const getSelectedAgency = (selectedEncryptedId: string) =>
    props.agencyAssociations.filter(c => c.encryptedIdentifier === selectedEncryptedId)[0];

  const currentSelectedAgency = getSelectedAgency(selectedAccount);
  const hasNyAndNonNyNumbers = pipe(
    currentSelectedAgency,
    O.fromNullable,
    O.fold(
      () => false,
      claims => {
        const {agentNumbers} = claims;

        return agentNumbers.some(agentNum => agentNum.isNewYork) && agentNumbers.some(agentNum => !agentNum.isNewYork);
      }
    )
  );
  const agencyAssociationSelections = pipe(
    props.agencyAssociations,
    fromArray,
    O.map(associations => {
      return associations.map(claim => (
        <MenuItem key={`agency__${claim.maskedIdentifier}`} value={claim.encryptedIdentifier}>
          {claim.maskedIdentifier}
        </MenuItem>
      ));
    })
  );

  const currentSelectedAccountIsTheDefault =
    currentSelectedAgency.isDefault &&
    (hasNyAndNonNyNumbers ? currentSelectedAgency.isDefaultShowNewYork === showNy : true);
  const [isDefault, setIsDefault] = useState(false);

  useEffect(() => {
    setIsDefault(currentSelectedAccountIsTheDefault);
  }, [currentSelectedAccountIsTheDefault]);

  const isSwitchingAccount =
    currentSelectedAgency.encryptedIdentifier !== currentTaxIdNumber || currentNyFilteringStatus !== showNy;
  const isUpdatingDefaultAccount = isDefault && !currentSelectedAccountIsTheDefault;
  const nyStatusToSave =
    currentSelectedAgency.agentNumbers.length === 0
      ? false
      : hasNyAndNonNyNumbers
      ? showNy
      : currentSelectedAgency.agentNumbers[0].isNewYork;

  return (
    <Modal onClose={props.onClose} title="Switch Accounts" open>
      <div className="legacy-sign-on-form">
        <div>Please Select the Agency Account You Would Like to Use:</div>
        <FormControl className="legacy-sign-on-form__account-select">
          <InputLabel shrink={Boolean(selectedAccount)} htmlFor="account-select">
            {O.isNone(agencyAssociationSelections) ? 'No Accounts Available' : 'Account'}
          </InputLabel>
          <Select
            className="switch-tax-id-modal__tax-id-select"
            value={selectedAccount}
            inputProps={{
              id: 'account-select',
            }}
            disabled={O.isNone(agencyAssociationSelections)}
            onChange={event => {
              setAccount(event.target.value as string);
              setIsDefault(false);
            }}
          >
            {pipe(
              agencyAssociationSelections,
              O.fold(() => [<></>], identity)
            )}
          </Select>
          {hasNyAndNonNyNumbers && (
            <FormControlLabel
              className="switch-tax-id-modal__new-york-switch"
              control={
                <Switch
                  color="primary"
                  checked={showNy}
                  onChange={e => {
                    setShowNy(e.target.checked);
                  }}
                  value={showNy}
                />
              }
              label="View New York?"
            />
          )}
          {!isImpersonating && (
            <FormControlLabel
              control={
                <Switch
                  disabled={currentSelectedAccountIsTheDefault}
                  checked={isDefault}
                  onChange={e => setIsDefault(e.target.checked)}
                  value={isDefault}
                  color="primary"
                />
              }
              label={currentSelectedAccountIsTheDefault ? 'Selected Account Is Currently Default' : 'Use As Default'}
            />
          )}
        </FormControl>
        <Button
          className="legacy-sign-on-form__login-button"
          disabled={(!isSwitchingAccount && !isUpdatingDefaultAccount) || O.isNone(agencyAssociationSelections)}
          color="primary"
          variant="contained"
          fullWidth
          onClick={() => {
            if (isUpdatingDefaultAccount) {
              dispatch(
                updateDefaultAgencyAssociation(
                  props.claims.id,
                  currentSelectedAgency.encryptedIdentifier,
                  hasNyAndNonNyNumbers ? showNy : nyStatusToSave
                )
              );
            }

            if (isSwitchingAccount) {
              dispatch(
                setSelectedAgency(
                  O.some({
                    ...currentSelectedAgency,
                  }),
                  nyStatusToSave
                )
              );
            }
            props.onClose();
          }}
        >
          Apply
        </Button>
      </div>
    </Modal>
  );
};

export default withUserClaims(SwitchTaxIdModal);
